// src/components/dashboard/SensorMapLayer.js
import React from 'react';
import { Polyline, Marker, Popup, Tooltip } from 'react-leaflet';
import { Box, Text, Badge, VStack, HStack, Icon, Divider, useColorModeValue } from '@chakra-ui/react';
import { MdSensors } from 'react-icons/md';
import { FaDatabase } from 'react-icons/fa';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';

// Custom SVG Icon specifically for IoT Sensors
const createSensorIcon = (status) => {
  const color = status === 'active' ? '#805AD5' : status === 'error' ? '#E53E3E' : '#718096';
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 36 36">
      <circle cx="18" cy="18" r="14" fill="${color}" stroke="#fff" stroke-width="2.5" opacity="0.95"/>
      <g transform="translate(18,18) scale(0.45)" fill="white">
        <g transform="translate(-20,-20)">
          <path d="M20 2c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2s2-.9 2-2V4c0-1.1-.9-2-2-2zm-7.07 2.93c-.39-.39-1.02-.39-1.41 0-.39.39-.39 1.02 0 1.41L13.17 8c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41l-1.65-1.66zm14.14 0c-.39-.39-1.02-.39-1.41 0L24.01 6.59c-.39.39-.39 1.02 0 1.41.39.39 1.02.39 1.41 0l1.65-1.66c.39-.39.39-1.02 0-1.41zM4 12c0 1.1.9 2 2 2h4c1.1 0 2-.9 2-2s-.9-2-2-2H6c-1.1 0-2 .9-2 2zm8 8c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2s.9 2 2 2h4c1.1 0 2-.9 2-2zm14-8c0-1.1-.9-2-2-2h-4c1.1 0 2 .9 2 2s-.9 2-2 2h4c1.1 0 2-.9 2-2zM20 18c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2s2-.9 2-2v-4c0-1.1-.9-2-2-2z"/>
        </g>
      </g>
    </svg>`;
  return L.divIcon({
    className: 'custom-svg-icon',
    html: svg,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -16],
  });
};

const SensorMapLayer = ({ sensors = [], pipelines = [] }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const headingColor = useColorModeValue('gray.800', 'white');

  return (
    <>
      {/* ── 1. RENDER PIPELINES (Routes) ── */}
      {pipelines.map(pipeline => {
        let coords = [];
        try {
          // Parse the JSON coordinates from the database
          coords = typeof pipeline.path_coordinates === 'string'
            ? JSON.parse(pipeline.path_coordinates)
            : pipeline.path_coordinates;
        } catch (e) {
          console.warn(`Invalid pipeline coordinates for ${pipeline.id}`);
        }

        if (!coords || !Array.isArray(coords) || coords.length === 0) return null;

        return (
          <Polyline
            key={`pipe-${pipeline.id}`}
            positions={coords}
            pathOptions={{ color: '#319795', weight: 5, opacity: 0.7 }}
          >
            <Tooltip sticky className="custom-zone-tooltip">
              <Box textAlign="center">
                <Text fontWeight="bold" color="teal.700">{pipeline.name}</Text>
                <Text fontSize="xs">Material: {pipeline.material || 'Unknown'}</Text>
                <Text fontSize="xs">Diameter: {pipeline.diameter_inches ? `${pipeline.diameter_inches}"` : 'Unknown'}</Text>
              </Box>
            </Tooltip>
          </Polyline>
        );
      })}

      {/* ── 2. RENDER SENSORS (Clustered) ── */}
      <MarkerClusterGroup chunkedLoading={true} maxClusterRadius={40}>
        {sensors.map(sensor => {
          // Ensure valid coordinates
          if (!sensor.latitude || !sensor.longitude || isNaN(sensor.latitude) || isNaN(sensor.longitude)) return null;

          // Find the connected pipeline (Checks both the new polymorphic link and the legacy pipeline_id)
          let connectedPipeline = null;
          const targetPipeId = sensor.linked_entity_type === 'pipeline' ? sensor.linked_entity_id : sensor.pipeline_id;
          
          if (targetPipeId) {
            connectedPipeline = pipelines.find(p => p.id === targetPipeId);
          }

          return (
            <Marker
              key={`raw-sensor-${sensor.device_id}`}
              position={[parseFloat(sensor.latitude), parseFloat(sensor.longitude)]}
              icon={createSensorIcon(sensor.status)}
            >
              <Popup maxWidth={280} minWidth={220} className="custom-asset-popup" autoPan={false}>
                <Box p={2} bg={cardBg} borderRadius="md">
                  {/* Header */}
                  <HStack justify="space-between" mb={3}>
                    <HStack>
                      <Icon as={MdSensors} color={sensor.status === 'active' ? 'purple.500' : mutedColor} boxSize={4}/>
                      <Text fontWeight="800" fontSize="sm" color={headingColor}>{sensor.device_id}</Text>
                    </HStack>
                    <Badge colorScheme={sensor.status === 'active' ? 'green' : sensor.status === 'error' ? 'red' : 'gray'}>
                      {sensor.status || 'offline'}
                    </Badge>
                  </HStack>

                  <Divider mb={3} borderColor={borderColor}/>

                  {/* Body */}
                  <VStack align="start" spacing={1.5} fontSize="xs">
                    <Text><Text as="span" fontWeight="700" color={mutedColor}>Type:</Text> {sensor.sensor_type || sensor.type || 'Generic'}</Text>
                    <Text noOfLines={2}><Text as="span" fontWeight="700" color={mutedColor}>Location:</Text> {sensor.location_description || 'Unknown'}</Text>
                    
                    {/* Pipeline Connection Widget */}
                    {connectedPipeline ? (
                      <Badge colorScheme="teal" variant="subtle" mt={2} px={2} py={1} borderRadius="md" w="full" whiteSpace="normal">
                        🔗 Attached to: {connectedPipeline.name}
                      </Badge>
                    ) : (
                      <Badge colorScheme="orange" variant="outline" mt={2} px={2} py={1} borderRadius="md" w="full">
                        Not linked to a pipeline
                      </Badge>
                    )}
                  </VStack>
                </Box>
              </Popup>
            </Marker>
          );
        })}
      </MarkerClusterGroup>
    </>
  );
};

export default SensorMapLayer;